import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { PatientIndicatorsPDFWrapper } from "./PatientIndicatorsPDFWrapper";
import { SubcategoryChart } from "./SubcategoryChart";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Brain } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface PatientIndicadorsPDFContentProps {
  patientId: string;
  patient: any;
  onReady?: () => void;
}

type LabResultWithSubcategory = {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  subcategory_name: string | null;
  category_name: string | null;
};

type Subcategory = {
  id: string;
  name: string;
  category_name: string;
};

export function PatientIndicadorsPDFContent({ patientId, patient, onReady }: PatientIndicadorsPDFContentProps) {
  const [labResults, setLabResults] = useState<Map<string, LabResultWithSubcategory[]>>(new Map());
  const [enabledSubcategories, setEnabledSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [professional, setProfessional] = useState<any>(null);
  const [readyCalled, setReadyCalled] = useState(false);
  const [globalAnalysis, setGlobalAnalysis] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [patient.cpf]);

  // Notificar quando todos os dados estiverem carregados
  useEffect(() => {
    if (!loading && professional && !readyCalled) {
      // Delay para garantir que Recharts renderizou os SVGs completamente
      setTimeout(() => {
        setReadyCalled(true);
        onReady?.();
      }, 1000);
    }
  }, [loading, professional, onReady, readyCalled]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadChartPreferences(),
      loadLabResults(patient.cpf),
      loadProfessional(),
      loadGlobalAnalysis()
    ]);
    setLoading(false);
  };

  const loadGlobalAnalysis = async () => {
    const { data } = await supabase
      .from("ai_global_analysis_cache")
      .select("json_result")
      .eq("patient_id", patientId)
      .maybeSingle();

    if (data?.json_result) {
      setGlobalAnalysis(data.json_result);
    }
  };

  const loadProfessional = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (data) setProfessional(data);
  };

  const loadChartPreferences = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: subcategories } = await supabase
      .from("lab_subcategories")
      .select(`
        id,
        name,
        show_chart,
        lab_categories!inner (
          name
        )
      `)
      .eq("user_id", user.id)
      .eq("show_chart", true)
      .order("name");

    if (subcategories) {
      setEnabledSubcategories(
        subcategories.map((s: any) => ({
          id: s.id,
          name: s.name,
          category_name: s.lab_categories.name
        }))
      );
    }
  };

  const loadLabResults = async (cpf: string) => {
    const { data } = await supabase
      .from("lab_results_onboarding")
      .select(`
        id,
        test_name,
        value,
        unit,
        ref_low,
        ref_high,
        collected_at,
        created_at,
        subcategory_id,
        lab_subcategories (
          id,
          name,
          lab_categories (
            name
          )
        ),
        patients_onboarding!inner (
          cpf
        )
      `)
      .eq("patients_onboarding.cpf", cpf)
      .order("collected_at", { ascending: true });

    if (data) {
      const resultsWithSubcategory: LabResultWithSubcategory[] = data.map((r: any) => ({
        id: r.id,
        test_name: r.test_name,
        value: r.value,
        unit: r.unit,
        ref_low: r.ref_low,
        ref_high: r.ref_high,
        collected_at: r.collected_at,
        created_at: r.created_at,
        subcategory_id: r.subcategory_id,
        subcategory_name: r.lab_subcategories?.name ?? null,
        category_name: r.lab_subcategories?.lab_categories?.name ?? null
      }));

      setLabResults(groupResultsBySubcategory(resultsWithSubcategory));
    }
  };

  const groupResultsBySubcategory = (
    results: LabResultWithSubcategory[]
  ): Map<string, LabResultWithSubcategory[]> => {
    const map = new Map<string, LabResultWithSubcategory[]>();
    results.forEach((r) => {
      if (!r.subcategory_id) return;
      const existing = map.get(r.subcategory_id) || [];
      map.set(r.subcategory_id, [...existing, r]);
    });
    return map;
  };

  const getValueStatus = (
    value: number | null,
    refLow: number | null,
    refHigh: number | null
  ): "normal" | "low" | "high" => {
    if (value === null || refLow === null || refHigh === null) return "normal";
    if (value < refLow) return "low";
    if (value > refHigh) return "high";
    return "normal";
  };

  const calculateStats = () => {
    let totalMeasurements = 0;
    let outOfRange = 0;
    let normalRange = 0;

    labResults.forEach((results) => {
      results.forEach((r) => {
        if (r.value !== null) {
          totalMeasurements++;
          const status = getValueStatus(r.value, r.ref_low, r.ref_high);
          if (status !== "normal") {
            outOfRange++;
          } else {
            normalRange++;
          }
        }
      });
    });

    return {
      subcategoryCount: enabledSubcategories.length,
      totalMeasurements,
      outOfRange,
      normal: normalRange
    };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!patient || !professional) {
    return (
      <div className="text-center text-muted-foreground p-8">
        Carregando dados do paciente...
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <PatientIndicatorsPDFWrapper
      patient={patient}
      professional={professional}
      stats={stats}
    >
      {globalAnalysis && (
        <div className="mb-8" style={{ pageBreakAfter: "avoid" }}>
          <Card className="border-2 border-primary/20">
            <CardHeader className="bg-primary/5">
              <CardTitle className="flex items-center gap-2 text-primary">
                <Brain className="h-5 w-5" />
                Análise Global Inteligente
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {globalAnalysis.summary && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Resumo Clínico</h3>
                  <p className="text-sm leading-relaxed">{globalAnalysis.summary}</p>
                </div>
              )}
              {globalAnalysis.health_score && (
                <div className="flex items-center gap-4 py-2">
                  <div className="flex-shrink-0">
                    <div className="text-3xl font-bold text-primary">{globalAnalysis.health_score}/100</div>
                    <div className="text-xs text-muted-foreground">Score de Saúde</div>
                  </div>
                  <Progress value={globalAnalysis.health_score} className="flex-1" />
                </div>
              )}
              {globalAnalysis.highlights && globalAnalysis.highlights.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Destaques Clínicos</h3>
                  <div className="space-y-2">
                    {globalAnalysis.highlights.slice(0, 4).map((h: any, i: number) => (
                      <div key={i} className={`p-3 rounded border-l-4 text-sm ${h.type === "warning" ? "border-amber-500 bg-amber-50"
                        : h.type === "success" ? "border-green-500 bg-green-50"
                          : "border-blue-500 bg-blue-50"
                        }`}>
                        <div className="font-semibold mb-1">{h.title}</div>
                        <div className="text-xs">{h.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      <style>
        {`
          .pdf-charts-table {
            width: 100%;
            border-collapse: collapse;
          }
          .pdf-charts-table tr {
            page-break-inside: avoid;
          }
          .pdf-charts-table td {
            width: 100%;
            height: 280px;
            padding: 8px 0;
            vertical-align: top;
          }
          .chart-cell {
            width: 100%;
            height: 100%;
          }
        `}
      </style>
      <table className="pdf-charts-table">
        <tbody>
          {enabledSubcategories.map((subcategory) => {
            const results = labResults.get(subcategory.id) || [];
            if (results.length === 0) return null;

            return (
              <tr key={subcategory.id}>
                <td>
                  <div className="chart-cell">
                    <SubcategoryChart
                      subcategory={{
                        id: subcategory.id,
                        name: subcategory.name,
                        category_id: subcategory.id,
                        category_name: subcategory.category_name
                      }}
                      results={results}
                      period="all"
                      pdfMode={true}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </PatientIndicatorsPDFWrapper>
  );
}
